/**
 * Widget mode store extension. Reads `widgetMode`, `wm`, `lang`, `wuid`, `wpid`
 * from the URL once at boot and exposes typed getters for the rest of the app.
 *
 * `widgetMode = true` makes the booking funnel render its "light" (chrome-less)
 * variant suitable for being embedded in an iframe by the train-results widget.
 */
import {
    readWidgetPassengersPrefill,
    WidgetPassengersPrefill,
} from '@/logic/widgetMode/widgetPassengersPrefill'

/** Set to true to allow `?widgetMode=1` in the URL. Disabled until widget embed is ready. */
export const WIDGET_MODE_ENABLED = true

export type WidgetCheckoutMode = 'cart' | 'payment'

export interface WidgetModeState {
    enabled: boolean
    checkoutMode: WidgetCheckoutMode
    uuid: string
    partnerId: string
    passengersPrefill: WidgetPassengersPrefill | null
}

export const defaultWidgetModeState = (): WidgetModeState => ({
    enabled: false,
    checkoutMode: 'cart',
    uuid: '',
    partnerId: '',
    passengersPrefill: null,
})

export function readWidgetModeFromUrl(): WidgetModeState {
    try {
        if (!WIDGET_MODE_ENABLED) return defaultWidgetModeState()

        const params = new URLSearchParams(window.location.search)
        const enabled = params.get('widgetMode') === '1'
        if (!enabled) return defaultWidgetModeState()
        const wm = params.get('wm')
        const checkoutMode: WidgetCheckoutMode = wm === 'payment' ? 'payment' : 'cart'
        const uuid = params.get('wuid') || ''
        return {
            enabled: true,
            checkoutMode,
            uuid,
            partnerId: params.get('wpid') || '',
            passengersPrefill: readWidgetPassengersPrefill(params, uuid),
        }
    } catch {
        return defaultWidgetModeState()
    }
}

/** Vuex module: kept namespaced=false to match the rest of BookingStore. */
export const widgetModeModule = {
    state: (): { widgetMode: WidgetModeState } => ({
        widgetMode: defaultWidgetModeState(),
    }),
    mutations: {
        setWidgetMode(state: { widgetMode: WidgetModeState }, payload: WidgetModeState) {
            state.widgetMode = { ...payload }
        },
    },
    getters: {
        getIsWidgetMode: (state: { widgetMode: WidgetModeState }) => state.widgetMode.enabled,
        getWidgetCheckoutMode: (state: { widgetMode: WidgetModeState }) => state.widgetMode.checkoutMode,
        getWidgetMeta: (state: { widgetMode: WidgetModeState }) => state.widgetMode,
        getWidgetPassengersPrefill: (state: { widgetMode: WidgetModeState }) =>
            state.widgetMode.passengersPrefill,
        hasWidgetPassengersPrefill: (state: { widgetMode: WidgetModeState }) => {
            const list = state.widgetMode.passengersPrefill?.list
            if (!list?.length) return false
            return list.some(
                (slot) =>
                    !!(slot.firstname?.trim() ||
                        slot.lastname?.trim() ||
                        slot.birthdate?.trim() ||
                        slot.gender?.trim() ||
                        slot.title?.trim()),
            )
        },
    },
}
